import {AsyncPipe} from '@angular/common';
import {Component, DestroyRef, inject, Inject, Input, OnInit, signal} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {Router} from '@angular/router';
import {TranslateModule} from '@ngx-translate/core';
import {Select} from '@ngxs/store';
import {
    asyncScheduler,
    combineLatestWith,
    distinctUntilChanged,
    fromEvent,
    map,
    Observable,
    observeOn,
    shareReplay,
    startWith,
    take,
} from 'rxjs';
import {AuthenticationService} from 'src/app/core/auth/authentication.service';
import {LoadedTrackingEventsEnum} from 'src/app/enums/loaded-tracking-events.enum';
import {ViewTypeEnum} from 'src/app/enums/view-type.enum';
import {IProfile} from 'src/app/interfaces/profile';
import {isShowShoppingCart} from 'src/app/shared/helpers/show-shopping-cart.helper';
import {DS_VIEW_TYPE_INJECTION_TOKEN} from 'src/app/shared/injection-token/injection-tokens';
import {CheckoutAppService} from 'src/app/shared/services/checkout-core/checkout-app.service';
import {CheckoutOrderService} from 'src/app/shared/services/checkout-core/checkout-order.service';
import {MainPageService} from 'src/app/shared/services/main-page.service';
import {WindowSizeService} from 'src/app/shared/services/window-size.service';
import {ProfileState} from 'src/app/store/profile/profile.state';

@Component({
    selector: 'app-second-header',
    templateUrl: './second-header.component.html',
    styleUrls: ['./second-header.component.scss'],
    standalone: true,
    imports: [TranslateModule, AsyncPipe],
})
export class SecondHeaderComponent implements OnInit {
    private readonly mainPageService = inject(MainPageService);

    @Input() public showGreeting: boolean = false;
    @Select(ProfileState.profile) public profile$: Observable<IProfile>;
    public name$: Observable<string>;

    get loggedIn(): boolean {
        return this.authService.loggedIn;
    }

    public readonly homePage$ = this.mainPageService.isPageFn$('home');
    public readonly isBookingPage$ = this.mainPageService.isBookingPage$;
    public readonly isMobile$ = this.windowSizeService.isMobile$;
    public readonly isWebView = this.viewType === ViewTypeEnum.EMBEDDED;
    public readonly landingPage$ = this.mainPageService.isPageFn$('landing');

    public readonly showMobileShoppingCart$ = this.homePage$.pipe(
        combineLatestWith(
            this.landingPage$,
            this.isMobile$,
            this.checkoutOrderService.state$.pipe(map((state) => isShowShoppingCart(state.data))),
        ),
        map(([homePage, landingPage, isMobile, cartNonEmpty]) => {
            return (homePage || landingPage) && isMobile && cartNonEmpty;
        }),
        distinctUntilChanged(),
        observeOn(asyncScheduler),
        startWith(false),
        shareReplay({refCount: true, bufferSize: 1}),
    );

    public readonly showBackButton$ = this.isMobile$.pipe(
        combineLatestWith(this.homePage$, this.landingPage$),
        map(([, landingPage, homePage]) => {
            return !(homePage || landingPage);
        }),
        distinctUntilChanged(),
        startWith(false),
    );

    public readonly showLabelFromProgressbar = signal(null);
    private readonly destroyRef = inject(DestroyRef);

    constructor(
        private readonly authService: AuthenticationService,
        private readonly router: Router,
        private readonly windowSizeService: WindowSizeService,
        private readonly checkoutAppService: CheckoutAppService,
        private readonly checkoutOrderService: CheckoutOrderService,
        @Inject(DS_VIEW_TYPE_INJECTION_TOKEN) private readonly viewType: string | null,
    ) {}

    public ngOnInit(): void {
        this.name$ = this.profile$.pipe(map((profile) => profile?.givenName));

        fromEvent(window, LoadedTrackingEventsEnum.VIEW_PROGRESSBAR_OPEN)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {});

        fromEvent(window, LoadedTrackingEventsEnum.VIEW_PROGRESSBAR_CLOSE)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                this.showLabelFromProgressbar.set(null);
            });

        fromEvent(window, LoadedTrackingEventsEnum.VIEW_PROGRESSBAR_STEP_LABEL)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((event: CustomEvent) => {
                this.showLabelFromProgressbar.set(event.detail.stepLabel);
            });
    }

    public back(): void {
        this.checkoutAppService.opened$.pipe(take(1)).subscribe((opened) => {
            if (opened) {
                this.closeCheckout();
            } else {
                this.router.navigate(['..']);
            }
        });
    }

    private closeCheckout(): void {
        this.checkoutAppService.closeCheckout();
    }
}
